import { useQuery } from '@apollo/client';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { UploadVomDeletePageQuery } from '~/apollo/generated/v4/graphql';
import { NotFound } from '~/components/common/NotFound';
import { SimplePanel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { DeleteVom } from '~/components/upload/vom/delete-vom';
import { useRouteParam } from '~/hooks/routing';

const UPLOAD_VOM_DELETE_PAGE = graphql(`
  query UploadVomDeletePage($id: ID!) {
    virtualOutcropModelGet(id: $id) {
      id
      pictures {
        id
      }
      cesiumAsset {
        id
      }
    }
  }
`);

export default function UploadVomDeletePage() {
  return (
    <ApolloProviderV4>
      <UploadVomDeletePageLoader />
    </ApolloProviderV4>
  );
}

function UploadVomDeletePageLoader() {
  const vomId = useRouteParam('vomId');

  const { data, loading } = useQuery(UPLOAD_VOM_DELETE_PAGE, {
    variables: { id: vomId },
  });

  if (loading) return <SpinnerPlaceholder />;
  if (!data?.virtualOutcropModelGet) return <NotFound />;

  return <UploadVomDeletePageInner vom={data.virtualOutcropModelGet} />;
}

function UploadVomDeletePageInner({
  vom,
}: {
  vom: NonNullable<UploadVomDeletePageQuery['virtualOutcropModelGet']>;
}) {
  const hasPictures = vom.pictures.length > 0;
  const hasCesiumAsset = !!vom.cesiumAsset;
  const canDelete = !hasPictures && !hasCesiumAsset;

  return (
    <SimplePanel title="Delete Virtual Outcrop Model">
      <div className="space-y-4">
        <div className="space-y-2">
          {hasPictures && (
            <Alert status="error">
              This VOM cannot be deleted because it has linked pictures.
            </Alert>
          )}

          {hasCesiumAsset && (
            <Alert status="error">
              This VOM cannot be deleted because it has a linked Cesium asset.
            </Alert>
          )}
        </div>

        <div className="text-center">
          <DeleteVom vomId={parseInt(vom.id)}>
            {(handleDelete, loading) => (
              <Button
                type="button"
                color="error"
                onClick={handleDelete}
                disabled={!canDelete}
                loading={loading}
                startIcon={<FontAwesomeIcon icon={faTrash} />}
              >
                Delete Virtual Outcrop Model
              </Button>
            )}
          </DeleteVom>
        </div>
      </div>
    </SimplePanel>
  );
}
